import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Flex } from 'antd';
import { useAppSelector } from '../../../shared/hooks/use-app-selector';

import createQueryConfig from '../../../queries';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { t } from '../../../utils/i18n';
import { isAdmin } from '../../../shared/utils';
import type { SubscriptionResponse } from '@shore/types';

type CollectInvoicesPageResponse = {
  id: string;
  object: 'hosted_page';
  type: 'collect_now';
  state: 'created' | 'requested' | 'succeeded' | 'cancelled' | 'acknowledged';
  url: string;
  business_entity_id: string;
  embed: boolean;
  created_at: number;
  updated_at: number;
  expires_at: number;
  resource_version: number;
};

function WarningIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M12 3L22 20H2L12 3Z"
        stroke="#FF4D4F"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M12 9V14"
        stroke="#FF4D4F"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17" r="1" fill="#FF4D4F" />
    </svg>
  );
}

export function OverdueInvoicesNotification() {
  const { queryClient, apiClient } = useGlobals();
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const { isLoading: isLoadingSettings, merchantSettings } =
    useMerchantSettings();

  const [isDismissed, setIsDismissed] = useState(false);

  const queryConfig = createQueryConfig(queryClient, apiClient);

  const merchantId = merchantSettings?.merchantId;

  const { data: token, isLoading: isLoadingToken } = useQuery(
    queryConfig.authToken()
  );

  const shouldFetchSubscription = !isLoadingSettings;

  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    isSuccess: isSubscriptionSuccess,
  } = useQuery<SubscriptionResponse>(
    queryConfig.subscription(merchantSettings, {
      enabled: shouldFetchSubscription,
    })
  );

  const dueInvoicesCount = subscription?.due_invoices_count ?? 0;
  const hasDueInvoices = dueInvoicesCount > 0;

  const shouldFetchCollectPage =
    isSubscriptionSuccess &&
    hasDueInvoices &&
    Boolean(merchantId) &&
    Boolean(token);

  const { data: collectInvoicesPage, isLoading: isLoadingCollectPage } =
    useQuery<CollectInvoicesPageResponse>({
      queryKey: ['collect_invoices_page', merchantId],
      queryFn: async () => {
        if (!merchantId) {
          throw new Error(
            'merchantId is required to fetch collect invoices page'
          );
        }

        const response =
          await apiClient.subscriptionV3.createCollectInvoicesPage({
            merchantId,
            token,
          });

        return response.json() as Promise<CollectInvoicesPageResponse>;
      },
      enabled: shouldFetchCollectPage,
      gcTime: Infinity,
    });

  const payUrl = collectInvoicesPage?.url;

  const isLoadingAnything =
    isLoadingSettings ||
    isLoadingToken ||
    isLoadingSubscription ||
    isLoadingCollectPage;

  const isUserAdmin = isAdmin({ currentAccount, currentMerchant });

  const shouldRenderBanner =
    !isDismissed && !isLoadingAnything && hasDueInvoices && Boolean(payUrl);

  if (!shouldRenderBanner) return null;

  return (
    <Alert
      type="warning"
      showIcon={false}
      closable
      onClose={() => setIsDismissed(true)}
      message={
        <Flex align="center" justify="center" gap={12}>
          <WarningIcon />

          <span>{t('overdue-invoices-notification.message-text')}</span>

          {isUserAdmin && (
            <Button
              type="primary"
              danger
              href={payUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ whiteSpace: 'nowrap' }}
              data-testid="overdue-invoices-cta-button"
            >
              {t('overdue-invoices-notification.cta-button')}
            </Button>
          )}
        </Flex>
      }
      style={{
        backgroundColor: '#FFF4F4',
        borderColor: '#FFCCC7',
        padding: '10px 16px',
      }}
    />
  );
}
